import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getActualUrl } from '../services/apiService';
import { urlCache } from '../services/urlCache';

const NotFoundHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [showNotFound, setShowNotFound] = useState(false);
  const didRunRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    
    const handleUnknownRoute = async () => {
      try {
        // Prevent multiple calls in StrictMode or component re-mounts
        if (didRunRef.current) return;
        didRunRef.current = true;
        
        setIsLoading(true);
        
        // Extract route name from current path
        const pathname = location.pathname || '/';
        const segments = pathname.split('/').filter(Boolean);
        
        // If no segments (root path), redirect to home
        if (segments.length === 0) {
          navigate('/', { replace: true });
          return;
        }

        // Define known React routes - don't call API for these
        const knownRoutes = [
          '/', '/ApplyForJob', '/applications', '/referrals', 
          '/earnings', '/profile', '/projects', '/about', 
          '/contact', '/privacy'
        ];
        
        // Check if current path is a known route
        if (knownRoutes.includes(pathname)) {
          // This shouldn't happen since these routes are handled by React Router
          // But if it does, just show 404
          setShowNotFound(true);
          setIsLoading(false);
          return;
        }

        console.log('[NotFoundHandler] Checking unknown route:', pathname);
        
        // Check if UrlRouter already called getActualUrl API
        if (urlCache.hasCalledActualUrl) {
          console.log('[NotFoundHandler] UrlRouter already called getActualUrl, showing 404');
          setShowNotFound(true);
          setIsLoading(false);
          return;
        }
        
        // Call getActualUrl API to check if this is a valid shortened URL
        const res = await urlCache.getActualUrl(() => getActualUrl());
        
        // Check if component was unmounted during API call
        if (cancelled) return;
        
        if (res?.success && res?.data) {
          const urlStr = (res.data || '').trim();
          
          if (urlStr) {
            console.log('[NotFoundHandler] Found actual URL:', urlStr);
            
            // Normalize the URL
            const normalizedUrl = urlStr.startsWith('http')
              ? urlStr
              : `${window.location.origin}${urlStr.startsWith('/') ? '' : '/'}${urlStr}`;

            let parsed;
            try {
              parsed = new URL(normalizedUrl);
            } catch (e) {
              console.warn('[NotFoundHandler] Failed to parse URL:', urlStr);
              setShowNotFound(true);
              return;
            }

            // Check for different route types
            const isApplyForJob = parsed.pathname.includes('/ApplyForJob');
            const isInterviewFeedback = parsed.pathname.includes('/InterviewFeedback') || 
                                       parsed.pathname.includes('/interviewFeedbackScreen');
            
            // Handle Interview Feedback routes
            if (isInterviewFeedback) {
              console.log('[NotFoundHandler] Detected InterviewFeedback URL, navigating to /interviews');
              
              // Navigate directly to interviews route
              if (!cancelled) {
                navigate('/interviews', { replace: true });
              }
              return;
            }
            
            // Check if it's an ApplyForJob route
            if (isApplyForJob) {
              // Parse parameters from the actual URL
              const params = Object.fromEntries(parsed.searchParams.entries());
              const jobId = params.jobId || params.positionId || params.id || params.budgetId || params.budgetCityId;
              
              // Create job data from URL parameters
              const maxSalary = params.salary ? Number(params.salary) : undefined;
              const jobData = {
                id: jobId || Math.random().toString(36).slice(2),
                jobId: jobId,
                jobName: params.budgetName || params.title || 'Job Details',
                title: params.budgetName || params.title || 'Job Details',
                cityName: params.cityName || params.city || '',
                pincode: params.pinCode || params.pincode || '',
                maxSalaryLimit: Number.isFinite(maxSalary) ? maxSalary : undefined,
                recruiterId: params.recruiterId || undefined,
                positionId: params.budgetCityId || params.positionId || undefined,
                location:
                  (params.cityName || params.city) && (params.pinCode || params.pincode)
                    ? `${params.cityName || params.city} - ${params.pinCode || params.pincode}`
                    : 'Location Not Specified',
                salary: Number.isFinite(maxSalary)
                  ? `Up to ₹${maxSalary.toLocaleString()} per month`
                  : 'Salary Not Disclosed',
                _params: params,
                _fromActualUrl: true,
              };

              // Store job data for the modal
              try {
                sessionStorage.setItem('pendingJobDetails', JSON.stringify(jobData));
              } catch (_) {}

              // Navigate to home and trigger job details modal
              if (!cancelled) {
                navigate('/', { replace: true });
                
                // Dispatch event to open job details modal
                setTimeout(() => {
                  if (!cancelled) {
                    window.dispatchEvent(
                      new CustomEvent('openJobDetails', { detail: { job: jobData } })
                    );
                  }
                }, 100);
              }
              
              return;
            } else {
              // For other valid URLs that are not handled above, navigate to the resolved path
              const targetPath = parsed.pathname;
              if (!cancelled) {
                navigate(targetPath, { replace: true });
              }
              return;
            }
          }
        }
        
        // If API call failed or returned no data, show 404
        console.log('[NotFoundHandler] No valid URL found, showing 404');
        setShowNotFound(true);
        
      } catch (error) {
        console.error('[NotFoundHandler] Error checking route:', error);
        setShowNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    handleUnknownRoute();
    
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  // Always show white container with loader, never show 404
  if (showNotFound || isLoading) {
    return (
      <div style={{
        background: 'white',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          width: '90%'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }}></div>
          <p style={{ marginTop: '1rem', color: '#6b7280', fontSize: '1rem' }}>
            {isLoading ? 'Checking URL...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default NotFoundHandler;
